import * as React from 'react';
import styled from 'styled-components';

import { Icon } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { colors } from '@creditornot/cb-ingredients';
import { useThemeMode } from '@creditornot/cb-provider';

import { useFormatMessage } from '../../../hooks/useFormatMessage';
import { Stack } from '../Stack';

const StyledStarButton = styled.button`
  display: flex;
  border: 0;
  background: transparent;
  padding: 0;
  padding-right: 20px;
  cursor: pointer;

  &:last-of-type {
    padding-right: 0;
  }
`;

type Props = {
  value: number;
  onChange: (value: number) => void;
  starsCount?: number;
};

export const DEFAULT_STARS_COUNT = 5;

export const RatingInput: React.FC<Props> = ({
  value,
  onChange,
  starsCount = DEFAULT_STARS_COUNT,
}) => {
  const format = useFormatMessage();

  const themeMode = useThemeMode();
  const defaultStarColor = themeMode === 'dark' ? colors.salt16 : colors.pepper16;

  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  function handleMouseEnter(index: number) {
    setHoveredIndex(index);
  }

  function handleMouseLeave() {
    setHoveredIndex(null);
  }

  function handleClick(index: number) {
    if (value !== index + 1) {
      onChange(index + 1);
    }
  }

  return (
    <Stack direction="row" onMouseLeave={handleMouseLeave}>
      {Array(starsCount)
        .fill(null)
        .map((_, index) => {
          const isHovered = typeof hoveredIndex === 'number' && index <= hoveredIndex;
          const isSelected = index < value;

          return (
            <StyledStarButton
              aria-pressed={isSelected}
              aria-label={format('components.feedback.star_button_label', { value: index + 1 })}
              key={index}
              onMouseEnter={() => handleMouseEnter(index)}
              onClick={() => handleClick(index)}
              data-test-id="star-button"
            >
              <Icon
                source={icons.starFilled.medium}
                width={36}
                height={36}
                fill={
                  isSelected ? colors.banana100 : isHovered ? colors.banana48 : defaultStarColor
                }
              />
            </StyledStarButton>
          );
        })}
    </Stack>
  );
};
